<template>
  <div class="monthly-settlement">
    <div class="page-header">
      <h1 class="page-title">月结算表单</h1>
      <div class="page-actions">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          style="width: 280px; margin-right: 10px;"
          @change="handleDateRangeChange">
        </el-date-picker>
        <el-button type="primary" icon="el-icon-search" @click="fetchSettlementData">查询</el-button>
      </div>
    </div>

    <!-- 结算统计摘要 -->
    <el-card shadow="hover" class="summary-card">
      <div class="summary-header">
        <h3>{{ formatDateRangeTitle() }}结算摘要</h3>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="summary-item">
            <div class="label">订单总数</div>
            <div class="value">{{ summary.orderCount || 0 }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-item">
            <div class="label">未结算订单数</div>
            <div class="value">{{ summary.unsettledCount || 0 }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-item">
            <div class="label">总订单金额</div>
            <div class="value">¥{{ formatPrice(summary.totalAmount) }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="summary-item primary">
            <div class="label">未结算订单金额</div>
            <div class="value">¥{{ formatPrice(summary.unsettledAmount) }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 结算订单列表 -->
    <el-card shadow="hover" class="data-card">
      <div slot="header" class="card-header">
        <span>结算订单明细</span>
        <div>
          <el-button 
            type="success" 
            size="mini"
            icon="el-icon-download"
            @click="exportExcel">导出Excel</el-button>
          <el-button 
            v-if="settlementStatus === 'PENDING'" 
            type="primary" 
            size="mini"
            @click="confirmSettlement">确认结算</el-button>
        </div>
      </div>
      <el-table
        v-loading="loading"
        :data="settlementOrders"
        border
        style="width: 100%">
        <el-table-column prop="orderSn" label="订单编号" min-width="180"></el-table-column>
        <el-table-column prop="projectName" label="测试项目" min-width="150"></el-table-column>
        <el-table-column prop="customerName" label="客户名称" min-width="120"></el-table-column>
        <el-table-column label="样品数量" min-width="100">
          <template slot-scope="scope">
            {{ scope.row.testCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="订单金额" min-width="120">
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.actualPrice) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusType(scope.row.orderStatus)">
              {{ getStatusText(scope.row.orderStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              @click="viewOrderDetail(scope.row.id)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="queryParams.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="queryParams.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 确认对话框 -->
    <el-dialog
      title="确认结算"
      :visible.sync="confirmDialogVisible"
      width="500px">
      <div class="confirm-content">
        <p>您确定要确认{{ formatDateRangeTitle() }}的结算表单吗？</p>
        <p class="warning-text">确认后表示您已核对并同意此时间段的所有结算信息，将无法再次修改。</p>
        <div class="summary-info">
          <div class="summary-row">
            <span class="label">订单总数：</span>
            <span class="value">{{ summary.orderCount || 0 }} 个</span>
          </div>
          <div class="summary-row">
            <span class="label">未结算订单数：</span>
            <span class="value">{{ summary.unsettledCount || 0 }} 个</span>
          </div>
          <div class="summary-row">
            <span class="label">总订单金额：</span>
            <span class="value">¥{{ formatPrice(summary.totalAmount) }}</span>
          </div>
          <div class="summary-row">
            <span class="label">未结算订单金额：</span>
            <span class="value amount">¥{{ formatPrice(summary.unsettledAmount) }}</span>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="doConfirmSettlement" :loading="confirmLoading">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'MonthlySettlement',
  data() {
    // 获取当前年月
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    
    // 设置默认日期范围：当月第一天到当天
    const startDate = new Date(currentYear, currentMonth - 1, 1)
    const endDate = new Date()
    
    // 格式化日期为API需要的格式
    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      loading: false,
      confirmLoading: false,
      confirmDialogVisible: false,
      settlementId: null,
      settlementStatus: '', // PENDING-待确认, CONFIRMED-已确认

      // 查询参数
      queryParams: {
        year: currentYear,
        month: currentMonth,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        pageNum: 1,
        pageSize: 10
      },

      // 结算摘要
      summary: {
        orderCount: 0,
        unsettledCount: 0,
        totalAmount: 0,
        unsettledAmount: 0
      },

      // 结算订单列表
      settlementOrders: [],
      total: 0,
      
      // 日期范围
      dateRange: [startDate, endDate]
    }
  },
  created() {
    this.fetchSettlementData()
  },
  methods: {
    // 获取结算数据
    fetchSettlementData() {
      this.loading = true
      adminApi.getMerchantSettlementDetail({
        merchantId: adminApi.merchantId,
        year: this.queryParams.year,
        month: this.queryParams.month,
        startDate: this.queryParams.startDate,
        endDate: this.queryParams.endDate,
        pageNum: this.queryParams.pageNum,
        pageSize: this.queryParams.pageSize
      })
        .then(res => {
          if (res) {
            this.settlementId = res.id || null
            this.settlementStatus = res.status || ''
            this.summary = {
              orderCount: res.orderCount || 0,
              unsettledCount: res.unsettledCount || 0,
              totalAmount: res.totalAmount || 0,
              unsettledAmount: res.unsettledAmount || 0
            }
            this.settlementOrders = res.orders || []
            this.total = res.total || 0
          }
        })
        .catch(error => {
          console.error('获取月结算数据失败', error)
          this.$message.error('获取月结算数据失败: ' + (error.message || '未知错误'))
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 确认结算
    confirmSettlement() {
      if (!this.settlementId) {
        return this.$message.warning('没有可确认的结算单')
      }
      
      if (this.settlementStatus !== 'PENDING') {
        return this.$message.warning('只有待确认状态的结算单可以确认')
      }
      
      this.confirmDialogVisible = true
    },

    // 执行确认结算
    doConfirmSettlement() {
      if (!this.settlementId) {
        this.confirmDialogVisible = false
        return
      }
      
      this.confirmLoading = true
      
      adminApi.confirmSettlement({
        merchantId: adminApi.merchantId,
        year: this.queryParams.year,
        month: this.queryParams.month,
        startDate: this.queryParams.startDate,
        endDate: this.queryParams.endDate
      })
        .then(res => {
          if (res) {
            this.$message.success('结算单确认成功')
            this.settlementStatus = 'CONFIRMED'
            this.fetchSettlementData() // 刷新数据
          }
        })
        .catch(error => {
          console.error('结算单确认失败', error)
          this.$message.error('结算单确认失败: ' + (error.message || '未知错误'))
        })
        .finally(() => {
          this.confirmLoading = false
          this.confirmDialogVisible = false
        })
    },

    // 查看订单详情
    viewOrderDetail(id) {
      this.$router.push(`/merchant/orders/detail/${id}`)
    },

    // 格式化金额
    formatPrice(price) {
      if (price === undefined || price === null) return '0.00'
      return parseFloat(price).toFixed(2)
    },

    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '--'
      const date = new Date(dateStr)
      
      // 检查日期是否有效
      if (isNaN(date.getTime())) return dateStr
      
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },

    // 获取状态类型
    getStatusType(status) {
      const statusMap = {
        'NOT_SHIPPED': 'info',
        'IN_TRANSIT': 'warning',
        'TESTING': 'primary',
        'TESTED': 'success',
        'UNSETTLED': 'warning',
        'COMPLETED': 'success',
        'CANCEL': 'danger'
      }
      return statusMap[status] || 'info'
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'NOT_SHIPPED': '未邮寄',
        'IN_TRANSIT': '在途中',
        'TESTING': '测试中',
        'TESTED': '已测试',
        'UNSETTLED': '未结算',
        'COMPLETED': '已完成',
        'CANCEL': '已取消'
      }
      return statusMap[status] || status
    },

    // 分页相关方法
    handleSizeChange(size) {
      this.queryParams.pageSize = size
      this.fetchSettlementData()
    },
    
    handleCurrentChange(page) {
      this.queryParams.pageNum = page
      this.fetchSettlementData()
    },

    // 导出Excel
    exportExcel() {
      // 确认是否有数据可导出
      if (this.settlementOrders.length === 0) {
        return this.$message.warning('当前时间段没有结算数据可导出')
      }

      this.$message.info('正在准备导出数据，请稍候...')
      
      adminApi.exportMonthlySettlement({
        year: this.queryParams.year,
        month: this.queryParams.month,
        startDate: this.queryParams.startDate,
        endDate: this.queryParams.endDate
      })
        .then(response => {
          // 创建Blob对象
          const blob = new Blob([response.data])
          
          // 创建下载链接
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `结算单_${this.formatDateRangeTitle()}.xlsx`
          
          // 触发下载
          document.body.appendChild(link)
          link.click()
          
          // 清理
          setTimeout(() => {
            window.URL.revokeObjectURL(link.href)
            document.body.removeChild(link)
          }, 100)
          
          this.$message.success('导出成功')
        })
        .catch(error => {
          console.error('导出失败', error)
          this.$message.error('导出失败: ' + (error.message || '未知错误'))
        })
    },
    
    // 处理日期范围变化
    handleDateRangeChange(val) {
      if (val && val.length === 2) {
        const [startDate, endDate] = val
        this.queryParams.startDate = startDate
        this.queryParams.endDate = endDate
        
        // 保持兼容性：设置年月参数
        if (startDate) {
          const startDateObj = new Date(startDate)
          this.queryParams.year = startDateObj.getFullYear()
          this.queryParams.month = startDateObj.getMonth() + 1
        }
        
        this.fetchSettlementData()
      } else {
        this.queryParams.startDate = ''
        this.queryParams.endDate = ''
      }
    },
    
    // 格式化日期范围标题
    formatDateRangeTitle() {
      if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
        const startDate = this.formatDateForDisplay(this.dateRange[0])
        const endDate = this.formatDateForDisplay(this.dateRange[1])
        return `${startDate}至${endDate}`
      }
      return `${this.queryParams.year}年${this.queryParams.month}月`
    },
    
    // 格式化日期为显示格式
    formatDateForDisplay(date) {
      if (!date) return ''
      if (typeof date === 'string') {
        date = new Date(date)
      }
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      return `${year}年${month}月${day}日`
    }
  }
}
</script>

<style scoped>
.monthly-settlement {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
}

.page-actions {
  display: flex;
  align-items: center;
}

.summary-card {
  margin-bottom: 20px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.summary-header h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
}

.summary-item {
  text-align: center;
  padding: 20px 0;
}

.summary-item .label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.summary-item .value {
  font-size: 24px;
  font-weight: bold;
  color: #606266;
}

.summary-item.primary .value {
  color: #409EFF;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.data-card {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.confirm-content {
  padding: 20px 0;
}

.warning-text {
  color: #E6A23C;
  font-size: 14px;
}

.summary-info {
  margin-top: 20px;
}

.summary-row {
  margin-bottom: 10px;
}

.summary-row .label {
  font-size: 14px;
  color: #909399;
  margin-right: 10px;
}

.summary-row .value {
  font-size: 16px;
  font-weight: bold;
  color: #606266;
}

.summary-row .amount {
  color: #F56C6C;
}
</style> 